import React, { useEffect, useMemo, useRef, useState } from 'react'

const MwTextArea = ({id,value,onChange,label,placeholder,rows,invalid,invalidMsg,classNameCustom='',enableToolbar=false,rich=false,dir='auto',textAlign}) => {
    const textareaRef = useRef(null);
    const resolvedValue = value == null ? '' : String(value);
    const editorRef = useRef(null);
    const selectionRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);

    const minHeightPx = useMemo(() => {
        const base = Number.isFinite(Number(rows)) && Number(rows) > 0 ? Number(rows) * 22 : 140;
        return Math.max(120, Math.min(420, base));
    }, [rows]);

    const emitRichChange = () => {
        const el = editorRef.current;
        const html = el?.innerHTML ?? '';
        const text = (el?.textContent ?? '').replace(/\u00a0/g, ' ').trim();
        setIsEmpty(text.length === 0);
        onChange?.({ target: { name: id, value: html } });
    };

    const focusRich = () => {
        const el = editorRef.current;
        el?.focus?.();
    };

    const rememberSelection = () => {
        try {
            const root = editorRef.current;
            const sel = window.getSelection?.();
            if (!root || !sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            if (!root.contains(range.commonAncestorContainer)) return;
            selectionRef.current = range.cloneRange();
        } catch { }
    };

    const restoreSelection = () => {
        try {
            const root = editorRef.current;
            const sel = window.getSelection?.();
            const range = selectionRef.current;
            if (!root || !sel || !range) return;
            sel.removeAllRanges();
            sel.addRange(range);
        } catch { }
    };

    const exec = (cmd, valueArg) => {
        focusRich();
        try {
            restoreSelection();
            document.execCommand(cmd, false, valueArg);
        } finally {
            rememberSelection();
            emitRichChange();
        }
    };

    const toolbarButtonsRich = ([
        { key: 'bold', label: 'B', onClick: () => exec('bold') },
        { key: 'bullet', label: '•', onClick: () => exec('insertUnorderedList') },
        { key: 'number', label: '1.', onClick: () => exec('insertOrderedList') },
    ]);

    useEffect(() => {
        if (!rich) return;
        const el = editorRef.current;
        if (!el) return;
        if (isFocused) return;
        const nextHtml = resolvedValue || '';
        if (el.innerHTML !== nextHtml) {
            el.innerHTML = nextHtml;
            const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
            setIsEmpty(text.length === 0);
        }
    }, [isFocused, resolvedValue, rich]);

    const triggerChange = (nextValue, selectionStart, selectionEnd) => {
        onChange?.({ target: { name: id, value: nextValue } });
        if (selectionStart == null || selectionEnd == null) return;
        requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (!el) return;
            el.focus();
            el.selectionStart = selectionStart;
            el.selectionEnd = selectionEnd;
        });
    };

    const selectionInfo = () => {
        const el = textareaRef.current;
        if (!el) return { start: 0, end: 0 };
        return {
            start: Number.isFinite(Number(el.selectionStart)) ? el.selectionStart : 0,
            end: Number.isFinite(Number(el.selectionEnd)) ? el.selectionEnd : 0
        };
    };

    const applyWrap = (leftToken, rightToken = leftToken) => {
        const { start, end } = selectionInfo();
        const before = resolvedValue.slice(0, start);
        const middle = resolvedValue.slice(start, end);
        const after = resolvedValue.slice(end);
        const next = `${before}${leftToken}${middle}${rightToken}${after}`;
        triggerChange(next, start + leftToken.length, end + leftToken.length);
    };

    const applyLinePrefix = (type) => {
        const { start, end } = selectionInfo();
        const valueStr = resolvedValue;
        const lineStart = valueStr.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
        const lineEndCandidate = valueStr.indexOf('\n', end);
        const lineEnd = lineEndCandidate === -1 ? valueStr.length : lineEndCandidate;
        const selected = valueStr.slice(lineStart, lineEnd);
        const lines = selected.split('\n');

        if (type === 'bullet') {
            const allBulleted = lines.every((l) => l.trim() === '' || l.startsWith('- '));
            const nextLines = allBulleted
                ? lines.map((l) => (l.startsWith('- ') ? l.slice(2) : l))
                : lines.map((l) => (l.trim() === '' ? l : `- ${l}`));
            const nextSelected = nextLines.join('\n');
            const nextValue = valueStr.slice(0, lineStart) + nextSelected + valueStr.slice(lineEnd);
            const delta = nextSelected.length - selected.length;
            triggerChange(nextValue, start, end + delta);
            return;
        }

        if (type === 'number') {
            const numberRegex = /^\d+\.\s/;
            const allNumbered = lines.every((l) => l.trim() === '' || numberRegex.test(l));
            const nextLines = allNumbered
                ? lines.map((l) => (numberRegex.test(l) ? l.replace(numberRegex, '') : l))
                : lines.map((l, idx) => (l.trim() === '' ? l : `${idx + 1}. ${l}`));
            const nextSelected = nextLines.join('\n');
            const nextValue = valueStr.slice(0, lineStart) + nextSelected + valueStr.slice(lineEnd);
            const delta = nextSelected.length - selected.length;
            triggerChange(nextValue, start, end + delta);
        }
    };

    const toolbarButtons = ([
        { key: 'bold', label: 'B', onClick: () => applyWrap('**') },
        { key: 'bullet', label: '•', onClick: () => applyLinePrefix('bullet') },
        { key: 'number', label: '1.', onClick: () => applyLinePrefix('number') },
    ]);

    return (
        <>
            { label && 
                <label className='text-xs text-slate-500 ' htmlFor="inv_no">{label}</label>}
            {rich ? (
                <div className='flex items-center gap-2 mb-2'>
                    {toolbarButtonsRich.map((btn) => (
                        <button
                            key={btn.key}
                            type="button"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                rememberSelection();
                            }}
                            onClick={btn.onClick}
                            className='h-7 min-w-7 px-2 text-xs font-bold rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        >
                            {btn.label}
                        </button>
                    ))}
                </div>
            ) : enableToolbar && (
                <div className='flex items-center gap-2 mb-2'>
                    {toolbarButtons.map((btn) => (
                        <button
                            key={btn.key}
                            type="button"
                            onClick={btn.onClick}
                            className='h-7 min-w-7 px-2 text-xs font-bold rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        >
                            {btn.label}
                        </button>
                    ))}
                </div>
            )}
            {rich ? (
                <div className={`relative border w-full rounded p-2 text-xs text-slate-700 bg-white ${invalid ? 'border-red-500' : 'border-slate-200'} ${classNameCustom}`}>
                    <style>{`
                        .mw-rich-editor ol { list-style: decimal; padding-inline-start: 20px; margin: 0; }
                        .mw-rich-editor ul { list-style: disc; padding-inline-start: 20px; margin: 0; }
                        .mw-rich-editor li { margin: 4px 0; }
                        .mw-rich-editor a { color: #4f46e5; text-decoration: underline; }
                    `}</style>
                    {placeholder && isEmpty && !isFocused && (
                        <div
                            className="pointer-events-none absolute top-2 left-2 right-2 text-slate-400"
                            style={{ direction: dir, textAlign: textAlign || (dir === 'rtl' ? 'right' : dir === 'ltr' ? 'left' : 'start') }}
                        >
                            {placeholder}
                        </div>
                    )}
                    <div
                        id={id}
                        ref={editorRef}
                        contentEditable
                        suppressContentEditableWarning
                        className="mw-rich-editor"
                        onFocus={() => { setIsFocused(true); rememberSelection(); }}
                        onBlur={() => { rememberSelection(); setIsFocused(false); emitRichChange(); }}
                        onInput={() => { rememberSelection(); emitRichChange(); }}
                        onKeyUp={rememberSelection}
                        onMouseUp={rememberSelection}
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = e.clipboardData?.getData('text/plain') || '';
                            exec('insertText', text);
                        }}
                        dir={dir}
                        style={{ minHeight: minHeightPx, outline: 'none', whiteSpace: 'pre-wrap', direction: dir, textAlign: textAlign || (dir === 'rtl' ? 'right' : dir === 'ltr' ? 'left' : 'start') }}
                    />
                </div>
            ) : (
                <textarea 
                    ref={textareaRef}
                    className={` text-xs text-slate-600 border w-full rounded  p-2 ${ invalid && 'border border-red-500'} ${classNameCustom}`}
                    id={id}
                    name={id} 
                    value={resolvedValue} 
                    onChange={onChange}  
                    placeholder={placeholder}
                    rows={rows} 
                    dir={dir}
                    style={{resize: 'none', direction: dir, textAlign: textAlign || (dir === 'rtl' ? 'right' : dir === 'ltr' ? 'left' : 'start')}}
                />
            )}
            { invalidMsg && <div className='text-xs text-red-500'>{invalidMsg}</div> }

        </>
    )
}

export default MwTextArea
